// One Pulse metric card (docs/phase-0/slice6-component-designs.md §4.1
// `PulseMetricCard`; UX §4.1/§4.4/§4.6). `<h3>` is nesting depth only, never chosen for
// font size (component-designs.md §2.1) - the seven cards are a flat peer set under the
// "Your seven metrics" `<h2>` (`PulseTab.tsx`). Reuses `.kpi .n`/`.kpi .l`'s existing
// figure/label treatment (`app.css:770-779`, `brand.css:133-136`, component-designs.md
// §1's reuse table) rather than inventing a new figure style.
//
// Renders the metric's current value in its OWN unit (`format.js`'s deliberately
// unit-agnostic formatter, never a $/%/ms suffix the wire does not state), its
// `capturedAt` period, an optional trend readout, and an optional anomaly callout - or a
// stage-aware "no data yet" empty state naming what turns it on, never a blank card
// (UX §4.4).
import type { AnomalySignal, AppStage, MetricName, PulseMetricPoint } from "@nightcrew/shared";
import { pulseCopy, type PulseCopyKey } from "../copy/pulse.js";
import { formatMetricValue } from "../screens/pulse/format.js";
import { PulseAnomalyCallout } from "./PulseAnomalyCallout.js";
import { PulseMetricTrend, type PulseTrendData } from "./PulseMetricTrend.js";

/** The seven metric labels, in `MetricName`'s own declared order (pulse.ts:13-21) -
 * every value mapped, none left to guess (component-designs.md §4.1). */
const METRIC_LABEL_KEY: Record<MetricName, PulseCopyKey> = {
  active_users: "pulse.metric.label.active_users",
  signups: "pulse.metric.label.signups",
  retention: "pulse.metric.label.retention",
  revenue: "pulse.metric.label.revenue",
  errors: "pulse.metric.label.errors",
  p95: "pulse.metric.label.p95",
  uptime: "pulse.metric.label.uptime",
};

export const PulseMetricCard = ({
  metric,
  point,
  appStage,
  trend,
  anomaly,
}: {
  readonly metric: MetricName;
  /** `null` when no `metrics_snapshots` row exists yet for this metric (UX §4.4 "Empty"
   * row) - "a metric with no snapshot is simply absent," never fabricated
   * (pulse.service.ts's own header). */
  readonly point: PulseMetricPoint | null;
  readonly appStage: AppStage;
  readonly trend?: PulseTrendData;
  readonly anomaly?: AnomalySignal;
}): React.JSX.Element => (
  <article className="card pad" data-testid={`pulse-metric-${metric}`}>
    <div className="kpi">
      <h3 className="l">{pulseCopy(METRIC_LABEL_KEY[metric])}</h3>
      {point === null ? (
        <p className="muted mt8" style={{ fontSize: 13 }} data-testid="pulse-metric-empty">
          {pulseCopy(
            appStage === "validated" ? "pulse.metric.empty.validated" : "pulse.metric.empty.shipped",
          )}
        </p>
      ) : (
        <>
          <div className="n">{formatMetricValue(point.value)}</div>
          <p className="muted" style={{ fontSize: 12 }}>
            {pulseCopy("pulse.metric.asOf", {
              date: new Date(point.capturedAt).toLocaleDateString(),
            })}
          </p>
          {trend !== undefined ? <PulseMetricTrend trend={trend} /> : null}
          {anomaly !== undefined ? <PulseAnomalyCallout signal={anomaly} /> : null}
        </>
      )}
    </div>
  </article>
);
